// WHAT: Custom hook for aspect CRUD operations and updates
// WHY:  Encapsulates aspect management logic with type-safe updates
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: Tauri core API, aspect commands
// RISKS:
//   - NOTE: Changes are immediate but not persisted until save
//   - WARN: No validation of value type or range
//   - WARN: No validation of aspect name format or uniqueness within workspace

import type { AspectConfig, WorkspaceConfig } from "../types";

import { core } from "@tauri-apps/api";
import { useCallback } from "react";

/**
 * WHAT:
 *   Custom hook for managing aspect operations (create, update, delete).
 *
 * WHY:
 *   Provides type-safe aspect management with proper state updates and error handling.
 *
 * PARAMETERS:
 *   - config: Current workspace configuration
 *   - setConfig: Function to update configuration state
 *   - setError: Function to set error messages
 *   - setSuccess: Function to set success messages
 *   - setSaving: Function to set saving state
 *
 * RETURNS:
 *   Object containing aspect update and creation functions.
 *
 * @returns {object} Object with `addAspect`, `toggleAspectBoolean`, `updateAspect`, `updateAspectColor`, `updateAspectHours`, `updateAspectStatus` functions
 *
 * @param config
 *
 * @param setConfig
 *
 * @param setError
 *
 * @param setSuccess
 *
 * @param setSaving
 *
 */
export function useAspectManagement(
  config: null | WorkspaceConfig,
  setConfig: React.Dispatch<React.SetStateAction<null | WorkspaceConfig>>,
  setError: (error: null | string) => void,
  setSuccess: (success: null | string) => void,
  setSaving: (saving: boolean) => void,
) {
  /**
   * WHAT:
   *   Updates a specific aspect field value in the configuration state.
   *
   * WHY:
   *   Provides type-safe way to update aspect properties while maintaining immutability.
   *
   * PARAMETERS:
   *   - workspaceName: Workspace containing the aspect
   *   - aspectName: Aspect to update
   *   - field: Field name to update
   *   - value: New value for the field
   *
   * RISKS:
   *   - NOTE: Changes are immediate but not persisted until save
   *   - WARN: No validation of value type or range
   */
  const updateAspect = useCallback(
    (
      workspaceName: string,
      aspectName: string,
      field: keyof AspectConfig,
      value: boolean | number | string,
    ) => {
      if (!config) {
        return;
      }

      setConfig((previous) => {
        if (!previous) {
          return previous;
        }

        const newConfig = { ...previous };
        const workspace = newConfig.workspaces[workspaceName];
        if (workspace?.[aspectName]) {
          workspace[aspectName] = {
            ...workspace[aspectName],
            [field]: value,
          };
        }
        return newConfig;
      });
    },
    [config, setConfig],
  );

  const updateAspectHours = useCallback(
    (workspaceName: string, aspectName: string, hours: string) => {
      const numberValue = Number.parseFloat(hours);
      if (!Number.isNaN(numberValue)) {
        updateAspect(workspaceName, aspectName, "hours", numberValue);
      }
    },
    [updateAspect],
  );

  const updateAspectStatus = useCallback(
    (workspaceName: string, aspectName: string, status: string) => {
      updateAspect(workspaceName, aspectName, "status", status);
    },
    [updateAspect],
  );

  const updateAspectColor = useCallback(
    (workspaceName: string, aspectName: string, color: string) => {
      updateAspect(workspaceName, aspectName, "color", color);
    },
    [updateAspect],
  );

  const toggleAspectBoolean = useCallback(
    (
      workspaceName: string,
      aspectName: string,
      field: "carryover" | "count_hours" | "count_plan",
    ) => {
      if (!config) {
        return;
      }

      const currentValue = config.workspaces[workspaceName]?.[aspectName]?.[field];
      if (currentValue !== undefined) {
        updateAspect(workspaceName, aspectName, field, !currentValue);
      }
    },
    [config, updateAspect],
  );

  /**
   * WHAT:
   *   Creates a new aspect in a workspace with specified configuration.
   *
   * WHY:
   *   Allows users to add new aspects to workspaces for time tracking organization.
   *
   * PARAMETERS:
   *   - workspaceName: Workspace to add aspect to
   *   - aspectName: Name of the new aspect
   *   - aspectConfig: Configuration for the new aspect
   *   - onSuccess: Callback to execute after successful creation
   *
   * SIDE EFFECTS:
   *   - Creates aspect in database via backend command
   *   - Displays success/error messages
   *
   * RISKS:
   *   - WARN: No validation of aspect name format or uniqueness within workspace
   *   - NOTE: Aspect name is trimmed but not validated
   */
  const addAspect = useCallback(
    async (
      workspaceName: string,
      aspectName: string,
      aspectConfig: AspectConfig,
      onSuccess?: () => void,
    ) => {
      if (!workspaceName || !aspectName.trim()) {
        setError("Workspace and aspect name are required");
        return;
      }

      try {
        setSaving(true);
        setError(null);
        setSuccess(null);

        await core.invoke("add_aspect_cmd", {
          aspectConfig,
          aspectName: aspectName.trim(),
          workspaceName,
        });
        setSuccess(`✅ Aspect "${aspectName}" created successfully!`);
        onSuccess?.();
      } catch (error) {
        const message = error instanceof Error ? error.message : "❌ Error creating aspect";
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [setError, setSuccess, setSaving],
  );

  return {
    addAspect,
    toggleAspectBoolean,
    updateAspect,
    updateAspectColor,
    updateAspectHours,
    updateAspectStatus,
  };
}
